import axios from 'axios';

import api, { apiRequest } from 'mastodon/api';
import type {
  ApiGroupChatJSON,
  ApiGroupChatMessageJSON,
} from 'mastodon/api_types/group_chats';
import type { AppDispatch, GetState } from 'mastodon/store';
import type { PlaintextPayload } from 'mastodon/features/direct_messages/e2ee';
import {
  encryptGroupMessage,
  decryptGroupMessage,
  fetchAndProcessSenderKeys,
  uploadSenderKeyDistribution,
  rekeyGroup,
  purgeGroupEpoch,
  recordGroupDistIdSeen,
  ensureE2eeReady,
  getMyDeviceId,
  isWasmSupported,
  rememberSent,
  recallSent,
  rememberInbound,
  recallInbound,
  encryptAttachmentBytes,
  getRestoreCutoff,
} from 'mastodon/features/direct_messages/e2ee';
import { E2eeError } from 'mastodon/features/direct_messages/e2ee/wasm_errors';

// Media staged by the composer for an E2EE send: the local bytes, never an
// uploaded media_attachment id (H-2 — plaintext must never be uploaded).
export interface ComposedMedia {
  blob: Blob;
}

// Typed client-side error codes surfaced to the group UI.
export const DIST_ID_ROLLBACK_CODE = 'e2ee_distid_rollback';

function makeDistIdRollbackError(groupChatId: string, distId: string): Error {
  const err = new Error(
    'This group’s encryption epoch was rolled back by the server — refusing to send. This can indicate tampering; ask the group owner to create a new group.',
  ) as Error & { code?: string; groupChatId?: string; distId?: string };
  err.code = DIST_ID_ROLLBACK_CODE;
  err.groupChatId = groupChatId;
  err.distId = distId;
  return err;
}

export function isDistIdRollbackError(err: unknown): boolean {
  return (
    (err as { code?: string } | null | undefined)?.code ===
    DIST_ID_ROLLBACK_CODE
  );
}

export function isIdentityKeyChangedError(err: unknown): boolean {
  return (
    err instanceof E2eeError && err.context.code === 'identity_key_changed'
  );
}

function isGroupDistIdMismatch(err: unknown): boolean {
  return (
    err instanceof E2eeError && err.context.code === 'group_distid_mismatch'
  );
}

// M-2 tamper bookkeeping: messages whose ciphertext failed the
// distribution-id binding check are PERMANENTLY refused (no auto-retry —
// retrying a tampered row cannot heal it) and never rendered.
const _distIdTamperedIds = new Set<string>();

// L-12: replay dedupe. A malicious server can re-serve an OLD ciphertext
// under a NEW message id; the (sender, device, distId, chain-iteration)
// tuple identifies the chain position, so the re-served row is dropped even
// though its server id is fresh. Bounded in-memory set per group (the
// server-id dedupe in the reducer stays as the primary guard).
const REPLAY_DEDUPE_CAP = 2000;
const _seenChainPositions = new Map<string, Set<string>>();

function checkAndRecordChainPosition(
  groupChatId: string,
  key: string,
): boolean {
  let set = _seenChainPositions.get(groupChatId);
  if (!set) {
    set = new Set();
    _seenChainPositions.set(groupChatId, set);
  }
  if (set.has(key)) return false;
  if (set.size >= REPLAY_DEDUPE_CAP) {
    // Bound memory: drop the whole set — a rare false-negative duplicate is
    // preferable to unbounded growth (the server-id dedupe still applies).
    set.clear();
  }
  set.add(key);
  return true;
}

// Test hook: reset module-level crypto/dedupe memos between tests.
export function __resetGroupChatCryptoStateForTests(): void {
  _distIdTamperedIds.clear();
  _seenChainPositions.clear();
  _decryptRetryCount.clear();
  _lastDistributedId.clear();
  _lastDistributedMembers.clear();
}

function ab2b64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++)
    binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}
import { me } from 'mastodon/initial_state';

export const GROUP_CHATS_FETCH_REQUEST = 'GROUP_CHATS_FETCH_REQUEST';
export const GROUP_CHATS_FETCH_SUCCESS = 'GROUP_CHATS_FETCH_SUCCESS';
export const GROUP_CHATS_FETCH_FAIL = 'GROUP_CHATS_FETCH_FAIL';

export const GROUP_CHAT_MESSAGES_FETCH_REQUEST =
  'GROUP_CHAT_MESSAGES_FETCH_REQUEST';
export const GROUP_CHAT_MESSAGES_FETCH_SUCCESS =
  'GROUP_CHAT_MESSAGES_FETCH_SUCCESS';
export const GROUP_CHAT_MESSAGES_FETCH_FAIL = 'GROUP_CHAT_MESSAGES_FETCH_FAIL';

export const GROUP_CHAT_SEND_SUCCESS = 'GROUP_CHAT_SEND_SUCCESS';

export const GROUP_CHAT_MESSAGES_NEW = 'GROUP_CHAT_MESSAGES_NEW';

export const GROUP_CHAT_CREATE_SUCCESS = 'GROUP_CHAT_CREATE_SUCCESS';

export const GROUP_CHAT_UPDATE_SUCCESS = 'GROUP_CHAT_UPDATE_SUCCESS';

// Multipart upload for the group avatar (owner-only — the server enforces
// via GroupChatPolicy#update?). `file === null` removes the avatar.
export const updateGroupChatAvatar =
  (groupChatId: string, file: File | null) =>
  async (dispatch: AppDispatch): Promise<ApiGroupChatJSON> => {
    const formData = new FormData();
    if (file) {
      formData.append('avatar', file);
    } else {
      formData.append('remove_avatar', 'true');
    }
    const { data } = await api().put<ApiGroupChatJSON>(
      `/api/v1/group_chats/${groupChatId}`,
      formData,
    );
    dispatch({ type: GROUP_CHAT_UPDATE_SUCCESS, groupChat: data });
    return data;
  };

export const GROUP_CHAT_MESSAGES_CLEAR = 'GROUP_CHAT_MESSAGES_CLEAR';

export const GROUP_CHATS_UNREAD_COUNT_SUCCESS =
  'GROUP_CHATS_UNREAD_COUNT_SUCCESS';

export const GROUP_CHAT_ADD_MEMBERS_SUCCESS = 'GROUP_CHAT_ADD_MEMBERS_SUCCESS';

export const GROUP_CHAT_LEAVE_SUCCESS = 'GROUP_CHAT_LEAVE_SUCCESS';

export const GROUP_CHAT_DELETE_SUCCESS = 'GROUP_CHAT_DELETE_SUCCESS';

export const GROUP_CHAT_MESSAGE_DELETE_SUCCESS =
  'GROUP_CHAT_MESSAGE_DELETE_SUCCESS';

export const GROUP_READ_RECEIPT = 'GROUP_READ_RECEIPT';

export const receiveGroupReadReceipt = (payload: {
  group_chat_id: string;
  reader_account_id: string;
  last_read_message_id: string;
}) => ({ type: GROUP_READ_RECEIPT, payload });

// Fetch list of group chats
export const fetchGroupChats = () => async (dispatch: AppDispatch) => {
  dispatch({ type: GROUP_CHATS_FETCH_REQUEST });
  try {
    const data = await apiRequest<ApiGroupChatJSON[]>('GET', 'v1/group_chats');
    dispatch({ type: GROUP_CHATS_FETCH_SUCCESS, groupChats: data });
  } catch (err) {
    dispatch({ type: GROUP_CHATS_FETCH_FAIL, error: err });
  }
};

// Cache of sent payload, keyed by message ID. The sender can't decrypt
// their own Sender Key messages (they don't process their own SKDM), so we
// remember the plaintext (+ media key/iv) at send time and look it up here.
const _sentPlaintextCache = new Map<string, PlaintextPayload>();
const SENT_CACHE_MAX = 500;

export function cacheSentGroupPlaintext(
  messageId: string,
  payload: PlaintextPayload,
): void {
  if (_sentPlaintextCache.size >= SENT_CACHE_MAX) {
    // Evict oldest entry (Map preserves insertion order).
    const firstKey = _sentPlaintextCache.keys().next().value;
    if (firstKey !== undefined) _sentPlaintextCache.delete(firstKey);
  }
  _sentPlaintextCache.set(messageId, payload);
}

export function clearSentGroupPlaintextCache(): void {
  _sentPlaintextCache.clear();
}

// Resolve the plaintext of an OWN group-chat message.
//
// The sender's PRIMARY device can't decrypt its own Sender Key message (there
// is no receive chain for self), so it reads the sent-plaintext cache. But a
// SIBLING device of the sender CAN decrypt it (it processed the sender device's
// SKDM) — and MUST cache the result, because the Sender Key receive chain
// ratchets forward and the same ciphertext cannot be decrypted twice. Without
// caching, an own message "successfully decrypted then later failed to decrypt"
// on a second device (the GC analogue of the DM sibling-decrypt bug).
//
// Order: sent cache → inbound cache (sibling already decrypted) → sibling
// decrypt + cache → '[sent by you]' label. `allowInboundCache` is false for
// edited messages (the ciphertext changed; must re-decrypt).
export async function resolveOwnGroupMessage(
  message: ApiGroupChatMessageJSON,
  { allowInboundCache = true }: { allowInboundCache?: boolean } = {},
  expectedDistId?: string,
): Promise<ApiGroupChatMessageJSON> {
  let cached: PlaintextPayload | undefined = _sentPlaintextCache.get(
    message.id,
  );

  if (!cached) {
    try {
      const stored = await recallSent(message.id);
      if (stored?.text !== undefined) cached = stored;
    } catch {
      /* IDB unavailable */
    }
  }

  if (!cached && allowInboundCache) {
    try {
      const stored = await recallInbound(message.id);
      if (stored?.text !== undefined) cached = stored;
    } catch {
      /* IDB unavailable */
    }
  }

  if (cached) {
    return {
      ...message,
      text: cached.text ?? '',
      ciphertext: null,
      media_key: cached.mediaKey,
      media_iv: cached.mediaIv,
      reply_to: cached.reply_to,
    };
  }

  // Sibling decrypt: we didn't send this (no sent cache) but we likely hold the
  // sender DEVICE's Sender Key. Decrypt once and cache it. M-2: only with the
  // thread's current distribution_id — without it we fail closed.
  if (isWasmSupported() && message.ciphertext && message.account) {
    if (!expectedDistId || _distIdTamperedIds.has(message.id)) {
      return { ...message, text: '[unable to decrypt]' };
    }
    try {
      await ensureE2eeReady();
      const payload = await decryptGroupMessage(
        message.account.id,
        message.sender_device_id,
        message.ciphertext,
        expectedDistId,
      );
      try {
        await rememberInbound(message.id, payload);
      } catch {
        /* non-fatal */
      }
      return {
        ...message,
        text: payload.text ?? '',
        ciphertext: null,
        media_key: payload.mediaKey,
        media_iv: payload.mediaIv,
        reply_to: payload.reply_to,
      };
    } catch (err) {
      if (isGroupDistIdMismatch(err)) {
        // M-2: tamper — permanently refuse this row (no retry, no render).
        _distIdTamperedIds.add(message.id);
        console.warn(
          '[E2EE] Group message failed the distribution-id check (possible replay/tamper), refusing:',
          message.id,
        );
      }
      /* fall through to the label */
    }
  }

  // Fallback label — KEEP the ciphertext: the sibling SKDM typically arrives
  // seconds later (the sender distributes to our newly linked device on its
  // next poll), and pollGroupChatMessages' still-encrypted recovery pass can
  // only retry rows that still hold their ciphertext. Nulling it here froze
  // the '[sent by you]' label on second devices forever (bounded by
  // MAX_DECRYPT_RETRIES on the retry side).
  return { ...message, text: '[sent by you]' };
}

// Decrypt a batch of group chat messages in-place.
// `expectedDistId` is the thread's CURRENT distribution_id (M-2): every
// ciphertext is bound to it before decryption, and rows whose embedded
// distId differs are refused as tamper (never retried, never rendered).
async function decryptGroupMessages(
  messages: ApiGroupChatMessageJSON[],
  expectedDistId?: string,
): Promise<ApiGroupChatMessageJSON[]> {
  if (!isWasmSupported()) return messages;
  const currentAccountId = me ? String(me) : '';
  // Post-restore history cutoff (parity with DMs): rows created before the
  // backup this device was restored from can never be decrypted here — the
  // sender-key receive chains in the backup have advanced past them. Hide
  // them instead of rendering '[unable to decrypt]' for the whole history.
  const restoreCutoff = await getRestoreCutoff().catch(() => null);
  const out: ApiGroupChatMessageJSON[] = [];
  for (const msg of messages) {
    if (restoreCutoff !== null && Date.parse(msg.created_at) < restoreCutoff) {
      continue;
    }
    if (msg.ciphertext && msg.account) {
      // Own messages: sent-cache / sibling-decrypt (+cache) / label.
      if (msg.account.id === currentAccountId) {
        out.push(await resolveOwnGroupMessage(msg, {}, expectedDistId));
        continue;
      }

      // Check IDB cache first — avoids re-decrypting when component re-mounts
      // after navigation (receive chain may have advanced past the message's iteration).
      try {
        const cached = await recallInbound(msg.id);
        if (cached?.text !== undefined) {
          out.push({
            ...msg,
            text: cached.text ?? '',
            ciphertext: null,
            media_key: cached.mediaKey,
            media_iv: cached.mediaIv,
            reply_to: cached.reply_to,
          });
          continue;
        }
      } catch {
        // IDB unavailable — fall through to decrypt
      }

      // M-2: without the thread's current distribution_id we cannot bind the
      // ciphertext to the group/epoch — fail closed. Same for rows already
      // found to be tampered (no auto-retry).
      if (!expectedDistId || _distIdTamperedIds.has(msg.id)) {
        out.push({ ...msg, text: '[unable to decrypt]' });
        continue;
      }

      try {
        const payload = await decryptGroupMessage(
          msg.account.id,
          msg.sender_device_id,
          msg.ciphertext,
          expectedDistId,
        );
        // L-12: drop server replays of an already-rendered chain position
        // (same ciphertext re-served under a NEW message id).
        if (payload.iteration !== undefined) {
          const posKey = `${msg.account.id}:${msg.sender_device_id}:${expectedDistId}:${payload.iteration}`;
          if (!checkAndRecordChainPosition(msg.group_chat_id, posKey)) {
            continue;
          }
        }
        out.push({
          ...msg,
          text: payload.text ?? '',
          ciphertext: null,
          media_key: payload.mediaKey,
          media_iv: payload.mediaIv,
          reply_to: payload.reply_to,
        });
        // Cache the decrypted text so re-fetches on navigation don't need to re-decrypt.
        try {
          await rememberInbound(msg.id, payload);
        } catch {
          /* non-fatal */
        }
      } catch (err) {
        if (isGroupDistIdMismatch(err)) {
          // M-2: tamper — permanently refuse (no auto-retry, no render).
          _distIdTamperedIds.add(msg.id);
          console.warn(
            '[E2EE] Group message failed the distribution-id check (possible replay/tamper), refusing:',
            msg.id,
          );
        }
        // Keep the ciphertext: discarding it makes the failure unrecoverable
        // (the poll cursor has already advanced past this row).
        out.push({ ...msg, text: '[unable to decrypt]' });
        console.warn('[E2EE] Group decrypt failed:', err);
        if (process.env.NODE_ENV !== 'production') {
          console.debug(
            '[E2EE] Group decrypt detail: sender=',
            msg.account?.id,
            'device=',
            msg.sender_device_id,
            'msgId=',
            msg.id,
          );
        }
      }
    } else {
      // H-1: a group row with NO ciphertext is never renderable content —
      // the server requires ciphertext on create, so this is a legacy row or
      // active tampering. Mark it so gc_message refuses its `text` and shows
      // the undecryptable placeholder instead.
      out.push({ ...msg, __unencrypted: true });
    }
  }
  return out;
}

// Persisted SKDM distribution state (localStorage, survives page reload).
// Stored as '{distId}|{sortedMembersCsv}' under key 'e2ee_dist:{groupChatId}'.
//
// Re-distributing on every page load causes `create_sender_key_distribution`
// to return the CURRENT chain iteration (not 0). Receivers who process that
// SKDM can only decrypt messages at iteration >= current, so all older messages
// become undecryptable. Persisting this state prevents unnecessary re-uploads.
function _loadPersistedDist(
  groupChatId: string,
): { distId: string; members: string } | null {
  try {
    const raw = localStorage.getItem(`e2ee_dist:${groupChatId}`);
    if (!raw) return null;
    const sep = raw.indexOf('|');
    if (sep < 0) return null;
    return { distId: raw.slice(0, sep), members: raw.slice(sep + 1) };
  } catch {
    return null;
  }
}

function _savePersistedDist(
  groupChatId: string,
  distId: string,
  members: string,
): void {
  try {
    localStorage.setItem(`e2ee_dist:${groupChatId}`, `${distId}|${members}`);
  } catch {
    // localStorage unavailable (private browsing, etc.) — in-memory only this session
  }
}

// In-memory cache to avoid localStorage reads on every poll call within a session.
const _lastDistributedId = new Map<string, string>();
const _lastDistributedMembers = new Map<string, string>();

async function ensureSenderKeyDistributed(
  groupChatId: string,
  distributionId: string,
  memberAccountIds: string[],
): Promise<void> {
  if (!isWasmSupported()) return;

  // M-2/M-6b send-side distId hygiene: refuse a distribution_id the server
  // served BEFORE for this group and then superseded (removal-rollback — old
  // epoch key material may still be live and the removed member kept the
  // iteration-0 seed), or one owned by a DIFFERENT group (cross-group
  // collision — the groups would share sender-key chain material).
  const distIdStatus = await recordGroupDistIdSeen(groupChatId, distributionId);
  if (distIdStatus === 'rollback') {
    throw makeDistIdRollbackError(groupChatId, distributionId);
  }

  const sortedMembers = [...memberAccountIds].sort().join(',');

  // Fast path: in-memory check (within same page session)
  if (
    _lastDistributedId.get(groupChatId) === distributionId &&
    _lastDistributedMembers.get(groupChatId) === sortedMembers
  )
    return;

  // Load persisted state from localStorage (survives page reload)
  const persisted = _loadPersistedDist(groupChatId);
  const prevDistId = persisted?.distId ?? null;
  const prevMembers = persisted?.members ?? null;

  let membersToDistribute: string[];

  if (prevDistId !== distributionId) {
    // DistributionId changed (re-key) or first ever distribution → distribute to ALL.
    // create_sender_key_distribution generates a fresh key at iteration 0 for new distIds.
    membersToDistribute = memberAccountIds;
  } else if (prevMembers !== sortedMembers) {
    // Same distId but member set grew (re-added member, N07 fix).
    // Only distribute to NEW members — existing members keep their original SKDM
    // at iteration 0 so they can still decrypt all old messages.
    const prevSet = new Set(prevMembers?.split(',').filter(Boolean) ?? []);
    membersToDistribute = memberAccountIds.filter((id) => !prevSet.has(id));
  } else {
    // Persisted state matches, but this is the first send of this session for
    // this group. Still run distributeKeysToMembers for ALL members so it can
    // detect identity key changes (e.g. a member reset their account via the
    // passphrase gate). Safe: createSenderKeyDistribution reuses stored
    // iteration-0 bytes from IDB; if no sessions need re-establishing,
    // uploadSenderKeyDistribution returns early without making an API call.
    membersToDistribute = memberAccountIds;
  }

  if (membersToDistribute.length === 0) {
    _lastDistributedId.set(groupChatId, distributionId);
    _lastDistributedMembers.set(groupChatId, sortedMembers);
    return;
  }

  try {
    await ensureE2eeReady();
    const distributedCount = await uploadSenderKeyDistribution(
      groupChatId,
      distributionId,
      membersToDistribute,
    );
    // Only cache completion when ALL targeted members received the SKDM.
    // A partial success (some sessions failed) must NOT poison the cache — the
    // missed recipients need to be retried on the next send.
    if (distributedCount < membersToDistribute.length) {
      console.warn(
        '[E2EE] Partial SKDM distribution:',
        distributedCount,
        '/',
        membersToDistribute.length,
        'succeeded — will retry on next send',
      );
      return;
    }
    _lastDistributedId.set(groupChatId, distributionId);
    _lastDistributedMembers.set(groupChatId, sortedMembers);
    _savePersistedDist(groupChatId, distributionId, sortedMembers);
    // M-6b: a NEW distId is now fully distributed — purge the OLD epoch's key
    // material (send side). crypto.ts's fetchAndProcessSenderKeysLocked purges
    // on the RECEIVE side when IT sees the change; both purges are idempotent
    // (removing already-removed keys is a no-op), so they compose safely.
    if (prevDistId && prevDistId !== distributionId) {
      await purgeGroupEpoch(prevDistId).catch((err) => {
        console.warn('[E2EE] Old-epoch purge failed (non-fatal):', err);
      });
    }
  } catch (err) {
    // H-3: an unacknowledged member identity-key change must BLOCK the send —
    // never swallow it into a warning and let the message go out over an
    // incomplete (or attacker-pinned) distribution.
    if (isIdentityKeyChangedError(err)) throw err;
    console.warn('[E2EE] Failed to distribute sender key:', err);
  }
}

// Fetch messages for a group chat
export const fetchGroupChatMessages =
  (groupChatId: string, maxId?: string) =>
  async (dispatch: AppDispatch, getState: GetState) => {
    // maxId must ride the REQUEST: without it the reducer blanks the loaded
    // window for the duration of an older-page (scroll-up) fetch.
    dispatch({ type: GROUP_CHAT_MESSAGES_FETCH_REQUEST, groupChatId, maxId });
    // M-2: the thread's current distribution_id, needed to bind every
    // ciphertext to the group/epoch before decrypting.
    let expectedDistId: string | undefined;
    try {
      // Fetch and process any pending sender keys before decrypting
      if (isWasmSupported()) {
        try {
          await ensureE2eeReady();
          const sawNewDevice = await fetchAndProcessSenderKeys(groupChatId);
          // group MD-e: a member device linked mid-conversation (or a re-key)
          // just distributed its sender key to us. Reciprocate — drop the
          // account-level distribution memo so ensureSenderKeyDistributed
          // re-enumerates devices and sends our sender key to the NEW device.
          // Bounded: only fires on an actual new-device/re-key event.
          if (sawNewDevice) {
            _lastDistributedId.delete(groupChatId);
            _lastDistributedMembers.delete(groupChatId);
          }
          // N07 fix: re-distribute our SKDM whenever distribution_id has changed.
          // Fetching fresh group info ensures re-added members receive our SKDM
          // without waiting for us to send a message. Only on initial load (not
          // pagination) to avoid extra API calls on every scroll-up.
          if (!maxId) {
            const chatInfo = await apiRequest<ApiGroupChatJSON>(
              'GET',
              `v1/group_chats/${groupChatId}`,
            );
            expectedDistId = chatInfo.distribution_id;
            await ensureSenderKeyDistributed(
              groupChatId,
              chatInfo.distribution_id,
              chatInfo.members.map((m) => m.id),
            );
          }
        } catch {
          // Non-fatal: we may still be able to decrypt with cached keys
        }
      }

      if (!expectedDistId) {
        expectedDistId = getState().group_chats.list.find(
          (gc) => gc.id === groupChatId,
        )?.distribution_id;
      }

      const params: Record<string, string> = {};
      if (maxId) params.max_id = maxId;
      const data = await apiRequest<ApiGroupChatMessageJSON[]>(
        'GET',
        `v1/group_chats/${groupChatId}/messages`,
        { params },
      );
      const decrypted = await decryptGroupMessages(data, expectedDistId);
      dispatch({
        type: GROUP_CHAT_MESSAGES_FETCH_SUCCESS,
        groupChatId,
        messages: decrypted,
        maxId,
      });
    } catch (err) {
      dispatch({
        type: GROUP_CHAT_MESSAGES_FETCH_FAIL,
        groupChatId,
        error: err,
      });
    }
  };

// Bounded retry budget for messages whose decrypt failed (group MD-e
// convergence): a permanently undecryptable row (sender wiped before we ever
// received their key) must not be re-attempted on every 3s poll forever.
// 20 polls ≈ a 60s recovery window — ample for SKDM reciprocation.
const _decryptRetryCount = new Map<string, number>();
const MAX_DECRYPT_RETRIES = 20;

// Poll for new messages since a given message ID. Used for realtime updates.
// Only dispatches an action if there are new messages — keeps reducer quiet.
export const pollGroupChatMessages =
  (groupChatId: string, sinceId?: string) =>
  async (dispatch: AppDispatch, getState: GetState) => {
    try {
      // Multi-device convergence (group MD-e): process pending sender keys on
      // EVERY poll, not just when new messages arrive. A member's newly linked
      // device distributes its SKDM asynchronously; in a silent conversation
      // no new message ever triggers the fetch, so existing members would
      // never learn about the device, never reciprocate their own sender key,
      // and the new device could never decrypt. (Reproduced e2e:
      // tests/e2e/test-gc-e2ee-new-device.mjs.)
      if (isWasmSupported()) {
        try {
          await ensureE2eeReady();
          const sawNewDevice = await fetchAndProcessSenderKeys(groupChatId);
          // group MD-e: reciprocate to a member device linked mid-conversation
          // (or a re-key) — drop the account-level distribution memo so
          // ensureSenderKeyDistributed re-enumerates devices and sends our
          // sender key to the NEW device. Bounded: only on an actual
          // new-device/re-key event, never on a steady-state poll.
          if (sawNewDevice) {
            _lastDistributedId.delete(groupChatId);
            _lastDistributedMembers.delete(groupChatId);
          }
          // N07 fix: re-distribute our SKDM if distribution_id has changed.
          // Uses cached Redux state (cheap, no extra API call). If the state is
          // stale, fetchGroupChatMessages (on next chat open) will catch it.
          const chat = getState().group_chats.list.find(
            (gc) => gc.id === groupChatId,
          );
          if (chat?.distribution_id) {
            await ensureSenderKeyDistributed(
              groupChatId,
              chat.distribution_id,
              chat.members.map((m) => m.id),
            );
          }
        } catch {
          // Non-fatal
        }
      }

      const params: Record<string, string> = {};
      if (sinceId) params.since_id = sinceId;
      const data = await apiRequest<ApiGroupChatMessageJSON[]>(
        'GET',
        `v1/group_chats/${groupChatId}/messages`,
        { params },
      );
      // M-2: current distribution_id for the ciphertext binding check.
      const expectedDistId = getState().group_chats.list.find(
        (gc) => gc.id === groupChatId,
      )?.distribution_id;
      if (data.length > 0) {
        const decrypted = await decryptGroupMessages(data, expectedDistId);
        dispatch({
          type: GROUP_CHAT_MESSAGES_NEW,
          groupChatId,
          messages: decrypted,
        });
      }

      // Retry messages whose decrypt previously failed — their ciphertext is
      // kept in state precisely so this recovery pass can pick them up once
      // the missing sender key arrives (the since_id cursor has long advanced
      // past them, so they never come back from the server).
      if (isWasmSupported()) {
        const stillEncrypted = getState().group_chats.messages.filter(
          (m) =>
            m.ciphertext &&
            !_distIdTamperedIds.has(m.id) && // M-2: tamper is never retried
            (_decryptRetryCount.get(m.id) ?? 0) < MAX_DECRYPT_RETRIES,
        );
        if (stillEncrypted.length > 0) {
          for (const m of stillEncrypted) {
            _decryptRetryCount.set(
              m.id,
              (_decryptRetryCount.get(m.id) ?? 0) + 1,
            );
          }
          const retried = await decryptGroupMessages(
            stillEncrypted,
            expectedDistId,
          );
          for (const m of retried) {
            if (!m.ciphertext) {
              _decryptRetryCount.delete(m.id);
              dispatch({
                type: GROUP_CHAT_MESSAGE_EDITED_REMOTE,
                groupChatId,
                message: m,
              });
            }
          }
        }
      }
      return data;
    } catch {
      return [];
    }
  };

// Send a message (encrypted via Sender Key). NEVER falls back to plaintext
// (H-1): if ciphertext cannot be produced — no distribution id, WASM
// unsupported, encryption/distribution failure, or a distId rollback — the
// send throws and the composer surfaces the error.
// Recognise the 409 the server returns when a message was encrypted under a
// superseded distribution_id (the client's cached key info was stale). The
// body carries the CURRENT id for the re-key + retry.
const staleDistributionId = (err: unknown): string | null => {
  if (!axios.isAxiosError(err)) return null;
  const body = err.response?.data as
    | { code?: string; distribution_id?: string }
    | undefined;
  return err.response?.status === 409 &&
    body?.code === 'stale_distribution_id' &&
    typeof body.distribution_id === 'string'
    ? body.distribution_id
    : null;
};

export const sendGroupChatMessage =
  (
    groupChatId: string,
    text: string,
    media?: ComposedMedia,
    distributionId?: string,
    replyInfo?: {
      replyToId: string;
      senderId: string;
      senderName?: string;
      snippet: string;
    },
    forwarded?: boolean,
    // Member account ids from the caller's cached chat. When present, the
    // send SKIPS the per-send GET /group_chats/:id that used to be half its
    // latency — the POST carries a distribution_id freshness claim and the
    // server 409s a stale one (see the retry below), which is strictly
    // stronger than the old fetch-then-hope window.
    memberIds?: string[],
  ) =>
  async (dispatch: AppDispatch) => {
    const body: Record<string, unknown> = {};
    let sentPayload: PlaintextPayload | undefined;

    // H-2: encrypt the LOCAL media bytes directly — the plaintext file is
    // never uploaded to /api/v2/media and never downloaded back. The AES
    // key/iv travel inside the Signal-encrypted payload only.
    let encryptedMediaB64: string | undefined;
    let mediaKey: string | undefined;
    let mediaIv: string | undefined;
    if (media && isWasmSupported()) {
      const plain = await media.blob.arrayBuffer();
      const enc = await encryptAttachmentBytes(plain);
      mediaKey = enc.key;
      mediaIv = enc.iv;
      encryptedMediaB64 = ab2b64(enc.ciphertext);
    }

    // Distribute (if owed) + encrypt under distId, filling `body`. Shared by
    // the cached-info fast path and the stale-409 re-key retry.
    // ensureSenderKeyDistributed also enforces the M-2/M-6b distId hygiene
    // gate (rollback / cross-group collision → typed refusal) and rethrows an
    // unacknowledged member identity-key change (H-3).
    const encryptInto = async (
      distId: string,
      members: string[],
    ): Promise<void> => {
      await ensureE2eeReady();
      await ensureSenderKeyDistributed(groupChatId, distId, members);
      const deviceId = await getMyDeviceId();
      const payload: PlaintextPayload = {
        text,
        ...(mediaKey && { mediaKey, mediaIv }),
        ...(replyInfo && {
          reply_to: {
            message_id: replyInfo.replyToId,
            sender_id: replyInfo.senderId,
            sender_name: replyInfo.senderName,
            snippet: replyInfo.snippet,
          },
        }),
      };
      const ct = await encryptGroupMessage(distId, payload);
      body.ciphertext = ct.ciphertextB64;
      body.ciphertext_type = ct.type;
      body.sender_device_id = deviceId ?? 1;
      body.text = '';
      body.distribution_id = distId;
      if (replyInfo) body.reply_to_id = replyInfo.replyToId;
      if (forwarded) body.forwarded = true;
      sentPayload = payload;
    };

    const refetchAndEncrypt = async (): Promise<void> => {
      const chatInfo = await apiRequest<ApiGroupChatJSON>(
        'GET',
        `v1/group_chats/${groupChatId}`,
      );
      await encryptInto(
        chatInfo.distribution_id,
        chatInfo.members.map((m) => m.id),
      );
    };

    if (distributionId && isWasmSupported()) {
      try {
        if (memberIds && memberIds.length > 0) {
          // Fast path: trust the cached distId/members — one round trip.
          await encryptInto(distributionId, memberIds);
        } else {
          // Legacy path (no cached members supplied): fetch first.
          await refetchAndEncrypt();
        }
      } catch (err) {
        // Never fall back to plaintext — fail fast so the caller surfaces the error.
        // Sending plaintext would silently violate E2EE invariant S1.
        console.error('[E2EE] Group encrypt failed — message not sent:', err);
        throw err;
      }
    } else {
      throw new Error(
        !isWasmSupported()
          ? 'WebAssembly is required for encrypted messaging and is not supported in this browser.'
          : 'Group chat encryption is not ready — no sender key distribution ID.',
      );
    }

    if (encryptedMediaB64) body.encrypted_media_base64 = encryptedMediaB64;

    let data: ApiGroupChatMessageJSON;
    try {
      data = await apiRequest<ApiGroupChatMessageJSON>(
        'POST',
        `v1/group_chats/${groupChatId}/messages`,
        { data: body },
      );
    } catch (postErr) {
      // The cached distribution_id was superseded (member removal re-key
      // landed after our last poll). Re-key against fresh group info and
      // retry ONCE — a second stale rejection is a real error.
      if (staleDistributionId(postErr) === null) throw postErr;
      await refetchAndEncrypt();
      if (encryptedMediaB64) body.encrypted_media_base64 = encryptedMediaB64;
      data = await apiRequest<ApiGroupChatMessageJSON>(
        'POST',
        `v1/group_chats/${groupChatId}/messages`,
        { data: body },
      );
    }
    // For our own encrypted messages, replace ciphertext with the payload
    // we know we sent. We can't decrypt our own Sender Key messages (the
    // sender doesn't process their own SKDM), so we stash the payload here
    // and also cache it for future fetch/poll calls.
    if (sentPayload && data.id) {
      cacheSentGroupPlaintext(data.id, sentPayload);
      void rememberSent(data.id, sentPayload).catch(() => {});
    }
    const forDispatch = sentPayload
      ? {
          ...data,
          text: sentPayload.text ?? '',
          ciphertext: null,
          media_key: sentPayload.mediaKey,
          media_iv: sentPayload.mediaIv,
          reply_to: sentPayload.reply_to,
        }
      : data;
    dispatch({
      type: GROUP_CHAT_SEND_SUCCESS,
      groupChatId,
      message: forDispatch,
    });
    return data;
  };

// Handle a group chat message received via streaming — decrypts before dispatching.
// The streaming server sends raw (encrypted) messages; we must decrypt them here
// so they appear with their plaintext rather than the ciphertext blob.
export const receiveGroupChatMessage =
  (message: ApiGroupChatMessageJSON) =>
  async (dispatch: AppDispatch, getState: GetState) => {
    let result: ApiGroupChatMessageJSON | null;
    const currentAccountId = me ? String(me) : '';

    // Post-restore history cutoff (parity with DMs): a live message older
    // than the restored backup can never be decrypted here — hide it.
    const restoreCutoff = await getRestoreCutoff().catch(() => null);
    if (
      restoreCutoff !== null &&
      Date.parse(message.created_at) < restoreCutoff
    ) {
      return;
    }

    // M-2: the thread's current distribution_id for the binding check.
    const expectedDistId = getState().group_chats.list.find(
      (gc) => gc.id === message.group_chat_id,
    )?.distribution_id;

    if (message.account?.id === currentAccountId) {
      // Own message — sent cache, or a sibling device decrypts + caches it.
      result = await resolveOwnGroupMessage(message, {}, expectedDistId);
    } else if (isWasmSupported() && message.ciphertext && message.account) {
      try {
        await ensureE2eeReady();
        // Check IDB plaintext cache first (avoids re-decryption on duplicate delivery)
        const cached = await recallInbound(message.id);
        if (cached?.text !== undefined) {
          result = {
            ...message,
            text: cached.text ?? '',
            ciphertext: null,
            media_key: cached.mediaKey,
            media_iv: cached.mediaIv,
            reply_to: cached.reply_to,
          };
        } else if (!expectedDistId || _distIdTamperedIds.has(message.id)) {
          // M-2: fail closed without the current distId; tamper never retried.
          result = { ...message, text: '[unable to decrypt]' };
        } else {
          let payload;
          try {
            payload = await decryptGroupMessage(
              message.account.id,
              message.sender_device_id,
              message.ciphertext,
              expectedDistId,
            );
          } catch (firstErr) {
            // M-2: a distId mismatch is tamper — do NOT auto-retry, do not
            // render; permanently refuse the row.
            if (isGroupDistIdMismatch(firstErr)) {
              _distIdTamperedIds.add(message.id);
              console.warn(
                '[E2EE] Group streaming message failed the distribution-id check (possible replay/tamper), refusing:',
                message.id,
              );
              throw firstErr;
            }
            // First failure: the sender's SKDM may not be processed yet
            // (new member, or a member's newly linked device — the classic
            // multi-device case). Fetch pending sender keys and retry once,
            // mirroring what the fetch/poll paths do up front. Steady-state
            // messages never pay for the fetch (hot path stays clean).
            await fetchAndProcessSenderKeys(message.group_chat_id);
            payload = await decryptGroupMessage(
              message.account.id,
              message.sender_device_id,
              message.ciphertext,
              expectedDistId,
            );
          }
          // L-12: drop server replays of an already-rendered chain position.
          if (payload.iteration !== undefined) {
            const posKey = `${message.account.id}:${message.sender_device_id}:${expectedDistId}:${payload.iteration}`;
            if (!checkAndRecordChainPosition(message.group_chat_id, posKey)) {
              return;
            }
          }
          result = {
            ...message,
            text: payload.text ?? '',
            ciphertext: null,
            media_key: payload.mediaKey,
            media_iv: payload.mediaIv,
            reply_to: payload.reply_to,
          };
          try {
            await rememberInbound(message.id, payload);
          } catch {
            /* non-fatal */
          }
        }
      } catch (err) {
        /* decrypt failed — show fallback text but KEEP the ciphertext: the
           poll cursor (since_id) has already moved past this row, so
           discarding the ciphertext would make the failure permanent. A
           later recovery pass can retry while the ciphertext survives. */
        result = { ...message, text: '[unable to decrypt]' };
        console.warn('[E2EE] Group streaming decrypt failed:', err);
        if (process.env.NODE_ENV !== 'production') {
          console.debug(
            '[E2EE] Group streaming decrypt detail: sender=',
            message.account?.id,
            'device=',
            message.sender_device_id,
            'msgId=',
            message.id,
          );
        }
      }
    } else if (!message.ciphertext) {
      // H-1: ciphertext-less rows never render server-supplied text.
      result = { ...message, __unencrypted: true };
    } else {
      result = message;
    }

    if (!result) return;
    dispatch({
      type: GROUP_CHAT_MESSAGES_NEW,
      groupChatId: message.group_chat_id,
      messages: [result],
    });
  };

// Create a new group chat and distribute sender keys
export const createGroupChat =
  (name: string, memberIds: string[]) => async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiGroupChatJSON>('POST', 'v1/group_chats', {
      data: { name, member_ids: memberIds },
    });
    dispatch({ type: GROUP_CHAT_CREATE_SUCCESS, groupChat: data });

    // Distribute our Sender Key to all members automatically
    if (isWasmSupported() && data.distribution_id) {
      try {
        await ensureE2eeReady();
        const allMemberIds = data.members.map((m) => m.id);
        // M-2: record the group's first distId (baseline for rollback
        // detection). A fresh group can only be 'new' here.
        await recordGroupDistIdSeen(data.id, data.distribution_id);
        await uploadSenderKeyDistribution(
          data.id,
          data.distribution_id,
          allMemberIds,
        );
      } catch (err) {
        console.warn('[E2EE] Failed to distribute sender keys on create:', err);
      }
    }

    return data;
  };

// Add members to group chat and distribute sender keys to them
export const addGroupChatMembers =
  (groupChatId: string, accountIds: string[]) =>
  async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiGroupChatJSON>(
      'POST',
      `v1/group_chats/${groupChatId}/add_members`,
      { data: { account_ids: accountIds } },
    );
    dispatch({ type: GROUP_CHAT_ADD_MEMBERS_SUCCESS, groupChat: data });

    // Distribute our Sender Key to ALL current members (not just the newly
    // added ones). New members need it to decrypt our future messages; the
    // distribution to existing members is harmless and ensures no gaps when
    // the member list changes (N07 partial fix — other members' SKDMs still
    // require those members to re-distribute, which happens on their next send).
    if (isWasmSupported() && data.distribution_id) {
      try {
        await ensureE2eeReady();
        // M-2/M-6b: refuse a rolled-back / cross-group-colliding distId.
        const distIdStatus = await recordGroupDistIdSeen(
          data.id,
          data.distribution_id,
        );
        if (distIdStatus === 'rollback') {
          console.warn(
            '[E2EE] Refusing SKDM distribution under a rolled-back distribution_id:',
            data.distribution_id,
          );
          return;
        }
        const allMemberIds = data.members.map((m) => m.id);
        await uploadSenderKeyDistribution(
          data.id,
          data.distribution_id,
          allMemberIds,
        );
        // Force re-distribution check on next send/fetch in case the
        // distribution_id or member list changed since our last distribution.
        _lastDistributedId.delete(data.id);
        _lastDistributedMembers.delete(data.id);
      } catch (err) {
        console.warn(
          '[E2EE] Failed to distribute sender keys to new members:',
          err,
        );
      }
    }
  };

// M-9: the server pushes `members_added` on the group timeline when a member
// is added. Surface an in-thread system notice so the history-exposure of
// adding a member (they can read past messages once encryption keys are
// updated) is visible to everyone, and refresh the list so the member set is
// current.
export const GROUP_CHAT_MEMBERS_ADDED = 'GROUP_CHAT_MEMBERS_ADDED';

export const receiveGroupMembersAdded =
  (payload: {
    group_chat_id: string;
    added_account_ids: string[];
    added_by_account_id: string;
  }) =>
  (dispatch: AppDispatch) => {
    dispatch({ type: GROUP_CHAT_MEMBERS_ADDED, payload });
    void dispatch(fetchGroupChats());
  };

// Ticket 115: the owner or an admin removes someone. The server rotates the
// group's encryption epoch; refetching the list picks up the new
// distribution_id and the smaller roster.
export const removeGroupChatMember =
  (groupChatId: string, accountId: string) => async (dispatch: AppDispatch) => {
    await apiRequest('DELETE', `v1/group_chats/${groupChatId}/remove_member`, {
      params: { account_id: accountId },
    });
    void dispatch(fetchGroupChats());
  };

// The server pushes `members_removed` to the remaining members and to the
// person removed. If that is us, the group leaves our list at once (the same
// as leaving it ourselves); otherwise refresh so the roster is current.
export const receiveGroupMembersRemoved =
  (payload: {
    group_chat_id: string;
    removed_account_ids: string[];
    removed_by_account_id: string;
  }) =>
  (dispatch: AppDispatch) => {
    if (me && payload.removed_account_ids.includes(String(me))) {
      dispatch({
        type: GROUP_CHAT_LEAVE_SUCCESS,
        groupChatId: payload.group_chat_id,
      });
      return;
    }
    void dispatch(fetchGroupChats());
  };

// Leave a group chat
export const leaveGroupChat =
  (groupChatId: string) => async (dispatch: AppDispatch) => {
    await apiRequest('POST', `v1/group_chats/${groupChatId}/leave`);
    dispatch({ type: GROUP_CHAT_LEAVE_SUCCESS, groupChatId });
  };

// Delete a group chat (owner only)
export const deleteGroupChat =
  (groupChatId: string) => async (dispatch: AppDispatch) => {
    await apiRequest('DELETE', `v1/group_chats/${groupChatId}`);
    dispatch({ type: GROUP_CHAT_DELETE_SUCCESS, groupChatId });
  };

// Delete a message
export const deleteGroupChatMessage =
  (groupChatId: string, messageId: string) => async (dispatch: AppDispatch) => {
    await apiRequest(
      'DELETE',
      `v1/group_chats/${groupChatId}/messages/${messageId}`,
    );
    dispatch({
      type: GROUP_CHAT_MESSAGE_DELETE_SUCCESS,
      groupChatId,
      messageId,
    });
  };

export const GROUP_CHAT_MESSAGE_EDIT_SUCCESS =
  'GROUP_CHAT_MESSAGE_EDIT_SUCCESS';
export const GROUP_CHAT_MESSAGE_EDITED_REMOTE =
  'GROUP_CHAT_MESSAGE_EDITED_REMOTE';

export const editGroupChatMessage =
  (
    groupChatId: string,
    messageId: string,
    distributionId: string,
    newText: string,
  ) =>
  async (dispatch: AppDispatch) => {
    if (!isWasmSupported()) {
      throw new Error('WebAssembly is required for encrypted messaging.');
    }

    try {
      await ensureE2eeReady();
      // M-2/M-6b: same distId hygiene gate as the send path — refuse to
      // re-encrypt an edit under a rolled-back / cross-group distId.
      const distIdStatus = await recordGroupDistIdSeen(
        groupChatId,
        distributionId,
      );
      if (distIdStatus === 'rollback') {
        throw makeDistIdRollbackError(groupChatId, distributionId);
      }
      const deviceId = await getMyDeviceId();
      const payload: PlaintextPayload = { text: newText };
      const ct = await encryptGroupMessage(distributionId, payload);

      const body: Record<string, unknown> = {
        ciphertext: ct.ciphertextB64,
        ciphertext_type: ct.type,
        sender_device_id: deviceId ?? 1,
      };

      const data = await apiRequest<ApiGroupChatMessageJSON>(
        'PATCH',
        `v1/group_chats/${groupChatId}/messages/${messageId}`,
        { data: body },
      );

      cacheSentGroupPlaintext(messageId, payload);
      void rememberSent(messageId, payload).catch(() => {});

      const forDispatch: ApiGroupChatMessageJSON = {
        ...data,
        text: newText,
        ciphertext: null,
      };
      dispatch({
        type: GROUP_CHAT_MESSAGE_EDIT_SUCCESS,
        groupChatId,
        message: forDispatch,
      });
    } catch (err) {
      throw err;
    }
  };

export const receiveGroupMessageEdited =
  (message: ApiGroupChatMessageJSON) =>
  async (dispatch: AppDispatch, getState: GetState) => {
    const currentAccountId = me ? String(me) : '';
    let result: ApiGroupChatMessageJSON;

    // Post-restore history cutoff (parity with DMs): an edit to a message
    // older than the restored backup can never be decrypted here — hide it.
    const restoreCutoff = await getRestoreCutoff().catch(() => null);
    if (
      restoreCutoff !== null &&
      Date.parse(message.created_at) < restoreCutoff
    ) {
      return;
    }

    // M-2: the thread's current distribution_id for the binding check.
    const expectedDistId = getState().group_chats.list.find(
      (gc) => gc.id === message.group_chat_id,
    )?.distribution_id;

    if (message.account?.id === currentAccountId) {
      // Own edited message — sent cache, or a sibling re-decrypts the new
      // ciphertext (inbound cache disabled: the edit changed the ciphertext).
      result = await resolveOwnGroupMessage(
        message,
        {
          allowInboundCache: false,
        },
        expectedDistId,
      );
    } else if (isWasmSupported() && message.ciphertext && message.account) {
      try {
        await ensureE2eeReady();
        if (!expectedDistId || _distIdTamperedIds.has(message.id)) {
          result = { ...message, text: '[unable to decrypt]' };
        } else {
          // Skip recallInbound cache — always re-decrypt edited ciphertext
          let payload;
          try {
            payload = await decryptGroupMessage(
              message.account.id,
              message.sender_device_id,
              message.ciphertext,
              expectedDistId,
            );
          } catch (firstErr) {
            // M-2: tamper — never auto-retried.
            if (isGroupDistIdMismatch(firstErr)) {
              _distIdTamperedIds.add(message.id);
              throw firstErr;
            }
            // The edit may be the first message we see from a member's new
            // device — fetch pending sender keys and retry once (same recovery
            // as receiveGroupChatMessage).
            await fetchAndProcessSenderKeys(message.group_chat_id);
            payload = await decryptGroupMessage(
              message.account.id,
              message.sender_device_id,
              message.ciphertext,
              expectedDistId,
            );
          }
          result = {
            ...message,
            text: payload.text ?? '',
            ciphertext: null,
            media_key: payload.mediaKey,
            media_iv: payload.mediaIv,
            reply_to: payload.reply_to,
          };
          try {
            await rememberInbound(message.id, payload);
          } catch {
            /* non-fatal */
          }
        }
      } catch (err) {
        // Keep the ciphertext so a later recovery pass can retry the edit.
        result = { ...message, text: '[unable to decrypt]' };
        console.warn('[E2EE] Group edit decrypt failed:', err);
      }
    } else if (!message.ciphertext) {
      // H-1: ciphertext-less rows never render server-supplied text.
      result = { ...message, __unencrypted: true };
    } else {
      result = message;
    }

    dispatch({
      type: GROUP_CHAT_MESSAGE_EDITED_REMOTE,
      groupChatId: message.group_chat_id,
      message: result,
    });
  };

export const clearGroupChatMessages = () => ({
  type: GROUP_CHAT_MESSAGES_CLEAR,
});

export const GROUP_CHAT_MESSAGE_REACTION_SUCCESS =
  'GROUP_CHAT_MESSAGE_REACTION_SUCCESS';
export const GROUP_CHAT_MESSAGE_REACTION_UPDATE =
  'GROUP_CHAT_MESSAGE_REACTION_UPDATE';

export const toggleGroupChatMessageReaction =
  (groupChatId: string, messageId: string, emoji: string, me: boolean) =>
  async (dispatch: AppDispatch) => {
    try {
      const data = await apiRequest<ApiGroupChatMessageJSON>(
        me ? 'DELETE' : 'POST',
        me
          ? `v1/group_chats/${groupChatId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`
          : `v1/group_chats/${groupChatId}/messages/${messageId}/reactions`,
        me ? undefined : { data: { emoji } },
      );
      dispatch({
        type: GROUP_CHAT_MESSAGE_REACTION_SUCCESS,
        message: data,
        groupChatId,
      });
    } catch (err) {
      console.error('[GC reaction] failed:', err);
    }
  };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const receiveGroupChatReaction = (
  payload: any,
  currentAccountId: string,
): any => ({
  type: GROUP_CHAT_MESSAGE_REACTION_UPDATE,
  payload: { ...payload, currentAccountId },
});

export const GC_TYPING = 'GC_TYPING';
export const GC_TYPING_CLEAR = 'GC_TYPING_CLEAR';

export const receiveGcTyping = (payload: {
  account_id: string;
  group_chat_id: string;
}) => ({
  type: GC_TYPING,
  accountId: payload.account_id,
  groupChatId: payload.group_chat_id,
});

export const clearGcTyping = (accountId: string, groupChatId: string) => ({
  type: GC_TYPING_CLEAR,
  accountId,
  groupChatId,
});

// Fetch unread count
export const fetchUnreadGroupChatCount =
  () => async (dispatch: AppDispatch) => {
    try {
      const data = await apiRequest<{ count: number }>(
        'GET',
        'v1/group_chats/unread_count',
      );
      dispatch({ type: GROUP_CHATS_UNREAD_COUNT_SUCCESS, count: data.count });
    } catch {
      // silently fail
    }
  };
